import React, { useEffect } from "react";

interface LoadingProps {
  duration?: number;
  onComplete?: () => void;
  text?: string;
  theme?: "gold" | "rainbow";
}

const Loading: React.FC<LoadingProps> = ({
  duration = 1500,
  onComplete,
  text = "🚀 Loading Magic...",
  theme = "gold",
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  const getBackgroundStyle = () => {
    if (theme === "gold") {
      return {
        background:
          "linear-gradient(135deg, #ffc107 0%, #ff8f00 25%, #ff6f00 50%, #e65100 75%, #bf360c 100%)",
        backgroundSize: "400% 400%",
      };
    } else {
      return {
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)",
        backgroundSize: "400% 400%",
      };
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
        `}
      </style>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 9999,
          opacity: 1,
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          animation: "gradientShift 2s ease-in-out infinite",
          ...getBackgroundStyle(),
        }}
      >
        {/* Loading Content */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            color: "white",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {/* Spinner */}
          <div
            style={{
              width: "60px",
              height: "60px",
              border: "4px solid rgba(255, 255, 255, 0.3)",
              borderTop: "4px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />

          {/* Loading Text */}
          <div
            style={{
              fontSize: "18px",
              fontWeight: 600,
              textAlign: "center",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          >
            {text}
          </div>
        </div>

        {/* Floating Particles */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {Array.from({ length: 20 }, (_, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                width: `${Math.random() * 6 + 2}px`,
                height: `${Math.random() * 6 + 2}px`,
                background: `rgba(255, 255, 255, ${Math.random() * 0.8 + 0.2})`,
                borderRadius: "50%",
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${
                  Math.random() * 3 + 2
                }s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Loading;
